import { AmountError, CurrencyMismatchError } from './errors';

/**
 * Currencies GOODBUILD can transact in. All are two-decimal currencies, which
 * is asserted by MINOR_UNIT_EXPONENT rather than assumed; adding a zero-decimal
 * currency (JPY) or a three-decimal one (KWD) means revisiting every display
 * path, so the exponent table exists to make that visible.
 */
export const CURRENCIES = ['EUR', 'USD', 'GBP', 'PLN'] as const;

export type Currency = (typeof CURRENCIES)[number];

export const MINOR_UNIT_EXPONENT: Readonly<Record<Currency, number>> = Object.freeze({
  EUR: 2,
  USD: 2,
  GBP: 2,
  PLN: 2,
});

export function isCurrency(value: unknown): value is Currency {
  return typeof value === 'string' && (CURRENCIES as readonly string[]).includes(value);
}

/**
 * Money is an integer count of minor units (cents, grosze) plus a currency.
 *
 * There are no floats anywhere in this package. Every rate is expressed in
 * basis points and every product is computed with integer arithmetic, so the
 * same inputs produce a bit-identical allocation on any machine. That is what
 * makes the waterfall replayable.
 */
export interface Money {
  readonly currency: Currency;
  /** Integer minor units. May be negative. */
  readonly amount: number;
}

/**
 * The largest absolute minor-unit amount this package will accept.
 *
 * Products of amount and basis points must stay inside Number.MAX_SAFE_INTEGER
 * for the integer maths in `rounding.ts` to be exact. 1e11 minor units is one
 * billion euro on a single line, and 1e11 * 10000 = 1e15 < 9.007e15.
 */
export const MAX_MINOR_UNITS = 100_000_000_000;

export function assertAmount(amount: number, label = 'amount'): asserts amount is number {
  if (!Number.isSafeInteger(amount)) {
    throw new AmountError(`${label} must be a safe integer number of minor units, got ${amount}`);
  }
  if (Math.abs(amount) > MAX_MINOR_UNITS) {
    throw new AmountError(
      `${label} exceeds MAX_MINOR_UNITS (${MAX_MINOR_UNITS}), got ${amount}. ` +
        'Exact integer arithmetic is no longer guaranteed above this bound.',
    );
  }
}

export function money(currency: Currency, amount: number): Money {
  assertAmount(amount);
  return Object.freeze({ currency, amount });
}

export function zero(currency: Currency): Money {
  return money(currency, 0);
}

export function assertSameCurrency(a: Money, b: Money): void {
  if (a.currency !== b.currency) {
    throw new CurrencyMismatchError(`Cannot combine ${a.currency} with ${b.currency}`);
  }
}

export function add(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return money(a.currency, a.amount + b.amount);
}

export function subtract(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return money(a.currency, a.amount - b.amount);
}

export function negate(a: Money): Money {
  return money(a.currency, -a.amount);
}

export function sum(currency: Currency, values: readonly Money[]): Money {
  let total = 0;
  for (const value of values) {
    if (value.currency !== currency) {
      throw new CurrencyMismatchError(`Cannot sum ${value.currency} into ${currency}`);
    }
    total += value.amount;
  }
  return money(currency, total);
}

export function isZero(a: Money): boolean {
  return a.amount === 0;
}

export function isNegative(a: Money): boolean {
  return a.amount < 0;
}

/** -1, 0 or 1. Throws on currency mismatch rather than ordering across currencies. */
export function compare(a: Money, b: Money): -1 | 0 | 1 {
  assertSameCurrency(a, b);
  if (a.amount < b.amount) return -1;
  if (a.amount > b.amount) return 1;
  return 0;
}

/**
 * Formats for display and for test failure messages. Not a localisation
 * function: the storefront formats with Intl in the presentation layer. This
 * exists so ledger assertions read in euro rather than in cents.
 */
export function formatMinor(currency: Currency, amount: number): string {
  const exponent = MINOR_UNIT_EXPONENT[currency];
  const divisor = 10 ** exponent;
  const sign = amount < 0 ? '-' : '';
  const abs = Math.abs(amount);
  const whole = Math.trunc(abs / divisor);
  const fraction = String(abs - whole * divisor).padStart(exponent, '0');
  return exponent === 0 ? `${sign}${whole} ${currency}` : `${sign}${whole}.${fraction} ${currency}`;
}

export function format(value: Money): string {
  return formatMinor(value.currency, value.amount);
}
